import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { ComponentType, ReactNode } from 'react'
import { Fragment, useContext } from 'react'
import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react'
import cn from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'

import BrandingContext from '../context/BrandingContext'

type TRenderContentArgs = {
  active: boolean
  checked: boolean
}

type TRadioCardItem = {
  label: string
  value: string
  icon: IconProp
  description?: ReactNode
  disabled?: boolean
  onClick?: () => void
  renderContent?: (args: TRenderContentArgs) => ReactNode
}

type TContainerProps = {
  children: ReactNode
}

type Props = {
  selected: string
  setSelected: (value: string) => void
  label?: string
  groupLabel?: string
  items: TRadioCardItem[]
  disabled?: boolean
  Container?: ComponentType<TContainerProps>
}

const RadioCards = (props: Props) => {
  const {
    selected,
    setSelected,
    label,
    groupLabel,
    items,
    disabled,
    Container,
  } = props

  const SafeContainer = Container || Fragment
  const accessibleLabel = groupLabel || label

  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding

  return (
    <HeadlessRadioGroup
      value={selected}
      onChange={setSelected}
      disabled={disabled}
    >
      {accessibleLabel && (
        <HeadlessRadioGroup.Label className="sr-only">
          {accessibleLabel}
        </HeadlessRadioGroup.Label>
      )}
      <SafeContainer>
        {items.map((item) => (
          <HeadlessRadioGroup.Option
            key={item.label}
            value={item.value}
            onClick={item.onClick || undefined}
            disabled={item.disabled}
            className={({ checked }) =>
              cn(
                'relative border flex-1 cursor-pointer rounded-md md:pl-4 md:pr-6 focus:outline-hidden',
                {
                  'flex items-center pl-4 py-3 space-x-2': !item.description,
                  'p-4': item.description,
                  [`bg-${primaryColorPalette}-50 border-transparent z-10 ring-2 ring-${primaryColorPalette}-500`]:
                    checked,
                  'bg-white border-gray-300 hover:bg-gray-50': !checked,
                },
              )
            }
          >
            {({ active, checked }) => (
              <>
                <FontAwesomeIcon
                  icon={item.icon}
                  size="lg"
                  className={cn({
                    [`text-${primaryColorPalette}-600`]: checked,
                    'text-gray-500': !checked,
                  })}
                />
                <div className={cn('flex-1', { 'mt-2': item.description })}>
                  <HeadlessRadioGroup.Label
                    as="p"
                    className="font-medium text-sm tracking-tight text-gray-900"
                  >
                    {item.label}
                  </HeadlessRadioGroup.Label>
                  {item.description && (
                    <HeadlessRadioGroup.Description
                      as="p"
                      className={cn('mt-1 text-xs', {
                        'text-gray-900': checked,
                        'text-gray-600': !checked,
                      })}
                    >
                      {item.description}
                    </HeadlessRadioGroup.Description>
                  )}
                  {checked && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className={`absolute top-2 right-2 text-${primaryColorPalette}-600`}
                    />
                  )}
                  {item.renderContent &&
                    item.renderContent({ active, checked })}
                </div>
              </>
            )}
          </HeadlessRadioGroup.Option>
        ))}
      </SafeContainer>
    </HeadlessRadioGroup>
  )
}

export type { Props as TRadioCardProps, TRadioCardItem, TRenderContentArgs }

export default RadioCards
